import React from 'react';
import {connect} from 'react-redux';
import {Card, CardBody} from 'reactstrap';
import {IReduxModelConfigsState, IReduxStoreState} from '@src/@interfaces';
import {cancelFetchModelConfig, fetchModelConfig, retryFetchModelConfig} from '@src/_redux/model-configs/actions';
import {IListConfig} from '@src/@interfaces/components/List/IListConfig';
import ConfigurableView from './ConfigurableView';
import {IFailedHttpRequest} from '@src/@interfaces/IFailedHttpRequest';
import {IFormConfig} from '@src/@interfaces/components/Form/IFormConfig';
import {ModelViewProps} from '@src/@types/components/ModelView';
import app from '@src/helpers/app';
import {IModelFormConfig} from '@src/@interfaces/components/Form/IModelFormConfig';
import {getComponentName} from '@src/helpers/getComponentName';
import RenderIf from '@src/components/RenderIf/RenderIf';
import {writeToClipboard} from '@src/helpers/clipboard';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';

export default abstract class ModelView<
        P extends ModelViewProps = ModelViewProps,
        TConfig extends IListConfig | IModelFormConfig | IImportConfig = any
> extends ConfigurableView<P, TConfig> {
    static configsContainerName: keyof Pick<IReduxModelConfigsState, 'listConfigs' | 'formConfigs' | 'importConfigs'>;

    render() {
        try {
            this.isConnectedToStore(false);
        } catch (error: any) {
            return this.renderUnconnectedToStoreException(error.message);
        }

        return super.render();
    }

    protected renderUnconnectedToStoreException(message: string, exampleOfConnection: string | null = null) {
        return <Card>
            <CardBody>
                <div className="alert alert-danger">
                    <div>{message}</div>

                    <RenderIf expression={exampleOfConnection} render={example => {
                        return <div className="mt-2">
                            <i>Example of connection:</i>
                            <code className="d-block">{example}</code>
                            <button
                                    type="button"
                                    className="btn btn-sm btn-info mt-1"
                                    onClick={() => {
                                        writeToClipboard(example).then();
                                    }}
                            >
                                <i className="fa fa-copy"/> Copy
                            </button>
                        </div>;
                    }}/>
                </div>
            </CardBody>
        </Card>;
    }

    protected getConfig(props?: Readonly<P>): TConfig | null {
        return this.getConfigByContainerName<TConfig>(props, 'configs');
    }

    protected getListConfig<TListConfig extends IListConfig = IListConfig>(props?: Readonly<P>): TListConfig | null {
        return this.getConfigByContainerName<TListConfig>(props, 'listConfigs');
    }

    protected getFormConfig<TFormConfig extends IFormConfig = IFormConfig>(props?: Readonly<P>): TFormConfig | null {
        return this.getConfigByContainerName<TFormConfig>(props, 'formConfigs');
    }

    protected getImportConfig<TImportConfig extends IImportConfig = IImportConfig>(props?: Readonly<P>): TImportConfig | null {
        return this.getConfigByContainerName<TImportConfig>(props, 'importConfigs');
    }

    protected getConfigError(props?: Readonly<P>): IFailedHttpRequest | null {
        return (props ?? this.props).errors[(props ?? this.props).controllerName] ?? null;
    }

    protected loadConfig(): void {
        if (this.props.nowLoadingModelConfigs[this.props.controllerName]) {
            return;
        }

        this.props.fetchModelConfig(this.props.controllerName);
    }

    protected cancelConfigLoading(props?: Readonly<P>): void {
        (props ?? this.props).cancelFetchModelConfig((props ?? this.props).controllerName);
    }

    protected retryLoadConfig(): void {
        this.props.retryFetchModelConfig(this.props.controllerName);
    }

    protected nowLoading(props?: Readonly<P>): boolean {
        return (props ?? this.props).controllerName in (props ?? this.props).nowLoadingModelConfigs;
    }

    protected getConfigsContainerPropertyName() {
        return (this.constructor as typeof ModelView).configsContainerName;
    }

    protected isConnectedToStore(silent = false): boolean {
        let errorMessage: string | null = null;
        const someNecessaryCallablePropsFromRedux = [
            'fetchModelConfig',
            'retryFetchModelConfig',
            'cancelFetchModelConfig',
        ];
        if (someNecessaryCallablePropsFromRedux.some(
                name => typeof this.props[name as keyof typeof this.props] !== 'function'
        )) {
            errorMessage = `${getComponentName(this)} must be connected to store!`;
        }

        if (!silent && errorMessage) {
            throw new Error(errorMessage);
        }

        return !errorMessage;
    }

    static getStoreConnector() {
        const configsContainerName = this.configsContainerName;
        if (!configsContainerName) {
            throw new Error('Invalid class configuration. Missing static [configsContainerName] property');
        }

        const mapToState = (state: IReduxStoreState) => ({
            nowLoadingModelConfigs: state.modelConfigs.nowLoadingModelConfigs,
            configs: state.modelConfigs[configsContainerName],
            listConfigs: state.modelConfigs.listConfigs,
            formConfigs: state.modelConfigs.formConfigs,
            importConfigs: state.modelConfigs.importConfigs,
            errors: state.modelConfigs.errors,
        });

        return connect(mapToState, {
            fetchModelConfig,
            retryFetchModelConfig,
            cancelFetchModelConfig
        });
    }

    // eslint-disable-next-line
    static getPageTitleByControllerName(controllerName: string, hasModelId: boolean = false): string | null {
        return null;
    }

    protected static tryToGetConfigByControllerName<
            TConfig extends IListConfig | IFormConfig | IImportConfig = IListConfig | IFormConfig | IImportConfig
    >(controllerName: string, shouldTryLoadMissingConfig = true): TConfig | null {
        if (!controllerName) {
            return null;
        }

        const store = app('store');
        const state: IReduxStoreState = store.getState();
        const containerName = this.configsContainerName;

        if (!(controllerName in state.modelConfigs[containerName])) {
            if (state.modelConfigs.nowLoadingModelConfigs[containerName]) {
                return null;
            }

            if (shouldTryLoadMissingConfig) {
                store.dispatch(fetchModelConfig(controllerName));
            }

            return null;
        }

        return state.modelConfigs[containerName][controllerName] as TConfig ?? null;
    }

    private getConfigByContainerName<TResultType>(
            props: undefined | Readonly<P>,
            containerName: 'configs' | 'listConfigs' | 'formConfigs' | 'importConfigs'
    ): TResultType | null {
        const receivedProps = props ?? this.props;
        const config: any = receivedProps[containerName][receivedProps.controllerName];

        return (config as TResultType) ?? null;
    }
}
